import React, { useState } from "react";
import { CustomerAssessmentProps } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import { RiskMeter, getRiskColor } from "./RiskMeter";
import { getRiskBadgeClass } from "./customerAssessmentConfig";

export const CustomerAssessment: React.FC<CustomerAssessmentProps> = ({
  assessment,
  natureOfBusiness,
  onUpdateRisk,
  onAddAssessment,
  recordVerificationStatus,
}) => {
  const isRecordVerified = recordVerificationStatus === "verified";
  const [expandedAssessment, setExpandedAssessment] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<"current" | "history">("current");

  const currentRating = assessment?.currentRiskRating || "medium";
  const riskAssessments = assessment?.riskAssessments || [];
  const latestAssessment = riskAssessments[0];

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      {/* Header with gradient */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Customer Assessment
            </h3>
            <p className="text-sm text-slate-300 mt-1">
              Risk assessment and customer profile information
            </p>
          </div>
          {onAddAssessment && !isRecordVerified && (
            <button
              onClick={onAddAssessment}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Assessment
            </button>
          )}
        </div>
      </div>

      {/* Current Risk Rating */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-500">Current Risk Rating</span>
            <div className="flex items-center gap-3 mt-1">
              <span
                className={`px-3 py-1.5 text-lg font-semibold rounded border capitalize ${getRiskBadgeClass(currentRating)}`}
              >
                {currentRating}
              </span>
              <RiskMeter risk={currentRating} />
            </div>
          </div>
          {/* AL-437: the whole block is suppressed when neither value is supplied, so
              the C1 sees no "Last Assessment" heading over an empty date and assessor. */}
          {latestAssessment &&
            (latestAssessment.assessmentDate || latestAssessment.assessedBy) && (
              <div className="text-right">
                <span className="text-sm text-gray-500">Last Assessment</span>
                {latestAssessment.assessmentDate && (
                  <p className="text-sm text-gray-900">
                    {formatDateShort(latestAssessment.assessmentDate)}
                  </p>
                )}
                {latestAssessment.assessedBy && (
                  <p className="text-xs text-gray-500">
                    by {latestAssessment.assessedBy}
                  </p>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 sm:px-6 border-b border-slate-200/60 overflow-x-auto">
        <div className="flex space-x-4 sm:space-x-6 min-w-max">
          <button
            onClick={() => setActiveTab("current")}
            className={`py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "current"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Current Assessment
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "history"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Assessment History ({riskAssessments.length})
          </button>
          {/* AL-352: "Risk Assessment Responses" tab removed. Its answers were
              matched to this record by a fragile first-word name/ABN heuristic
              that could attribute another entity's questionnaire to this
              record. Until an authoritative assessment id is persisted on the
              CDD record, the question-level responses tab is hidden. */}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {/*
          AL-438: the customer's declaration of the nature and purpose of the
          business relationship. Rendered above the assessment gate below,
          deliberately — it is recorded against the case rather than against a
          risk assessment, so gating it on `assessment` would hide it for any
          case that has no assessment block.

          Shown only on the current tab, since it is a present-state declaration
          rather than assessment history.

          Hidden entirely when absent: it is not required of INDIVIDUAL cases and
          not asked of child workflows, so roughly three quarters of live rows are
          legitimately null. An empty label would imply the customer declined to
          answer a question they were never asked.
        */}
        {activeTab === "current" && natureOfBusiness && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              Nature &amp; Purpose of Business Relationship
            </h4>
            <div className="relative overflow-hidden rounded-2xl">
              <div className="relative bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 rounded-2xl p-3 sm:p-5 border border-slate-400/30 shadow-lg shadow-slate-500/25">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-xs font-semibold text-slate-200 uppercase tracking-wide">
                    Declared by the customer
                  </p>
                </div>
                <p
                  className="text-sm font-medium text-white whitespace-pre-wrap break-words"
                  data-testid="nature-of-business"
                >
                  {natureOfBusiness}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "current" ? (
          assessment ? (
            <div className="space-y-6">
              {/* Customer Profile - Vibrant Gradient Cards */}
              {(assessment.customerCategory ||
                assessment.customerSegment ||
                assessment.relationshipStartDate) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Customer Profile
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {assessment.customerCategory && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-violet-500 via-violet-600 to-purple-700 rounded-2xl p-3 sm:p-5 border border-violet-400/30 shadow-lg shadow-violet-500/25">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-lg font-black text-white drop-shadow-lg">
                                {assessment.customerCategory}
                              </p>
                              <p className="text-xs font-semibold text-violet-100 mt-1">
                                Category
                              </p>
                            </div>
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                              <svg
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {assessment.customerSegment && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-sky-500 via-sky-600 to-cyan-700 rounded-2xl p-3 sm:p-5 border border-sky-400/30 shadow-lg shadow-sky-500/25">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-lg font-black text-white drop-shadow-lg">
                                {assessment.customerSegment}
                              </p>
                              <p className="text-xs font-semibold text-sky-100 mt-1">
                                Segment
                              </p>
                            </div>
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                              <svg
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {assessment.relationshipStartDate && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-5 border border-emerald-400/30 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-lg font-black text-white drop-shadow-lg">
                                {formatDateShort(
                                  assessment.relationshipStartDate,
                                )}
                              </p>
                              <p className="text-xs font-semibold text-emerald-100 mt-1">
                                Relationship Since
                              </p>
                            </div>
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                              <svg
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Products/Services */}
              {assessment.productsServices &&
                assessment.productsServices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Products & Services
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {assessment.productsServices.map((product, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full text-sm ring-1 ring-blue-200/60"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {/* Expected Activity */}
              {assessment.expectedActivity && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Expected Activity
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {assessment.expectedActivity.transactionTypes && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-3 sm:p-4 border border-blue-400/30 shadow-sm">
                          <p className="text-xs font-semibold text-blue-100">
                            Transaction Types
                          </p>
                          <p className="text-sm font-bold text-white mt-1">
                            {assessment.expectedActivity.transactionTypes.join(
                              ", ",
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                    {assessment.expectedActivity.expectedVolume && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-4 border border-emerald-400/30 shadow-sm">
                          <p className="text-xs font-semibold text-emerald-100">
                            Expected Volume
                          </p>
                          <p className="text-sm font-bold text-white mt-1">
                            {assessment.expectedActivity.expectedVolume}
                          </p>
                        </div>
                      </div>
                    )}
                    {assessment.expectedActivity.expectedValue && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 rounded-2xl p-3 sm:p-4 border border-amber-400/30 shadow-sm">
                          <p className="text-xs font-semibold text-amber-100">
                            Expected Value
                          </p>
                          <p className="text-sm font-bold text-white mt-1">
                            {assessment.expectedActivity.expectedValue}
                          </p>
                        </div>
                      </div>
                    )}
                    {assessment.expectedActivity.countriesInvolved && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 rounded-2xl p-3 sm:p-4 border border-purple-400/30 shadow-sm">
                          <p className="text-xs font-semibold text-purple-100">
                            Countries
                          </p>
                          <p className="text-sm font-bold text-white mt-1">
                            {assessment.expectedActivity.countriesInvolved.join(
                              ", ",
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Source of Wealth/Funds */}
              {(assessment.sourceOfWealth || assessment.sourceOfFunds) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Source of Wealth & Funds
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {assessment.sourceOfWealth && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-700 rounded-2xl p-3 sm:p-5 border border-indigo-400/30 shadow-lg shadow-indigo-500/25">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <p className="text-xs font-semibold text-indigo-100 uppercase tracking-wide">
                              Source of Wealth
                            </p>
                          </div>
                          <p className="text-sm font-medium text-white">
                            {assessment.sourceOfWealth}
                          </p>
                        </div>
                      </div>
                    )}
                    {assessment.sourceOfFunds && (
                      <div className="relative group overflow-hidden rounded-2xl">
                        <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-700 rounded-2xl p-3 sm:p-5 border border-teal-400/30 shadow-sm">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                            </div>
                            <p className="text-xs font-semibold text-teal-100 uppercase tracking-wide">
                              Source of Funds
                            </p>
                          </div>
                          <p className="text-sm font-medium text-white">
                            {assessment.sourceOfFunds}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Latest Risk Factors */}
              {latestAssessment &&
                latestAssessment.customerRiskFactors.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Risk Factors
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {latestAssessment.customerRiskFactors
                        .filter((factor) => !factor.factor.toLowerCase().includes("industry"))
                        .map(
                        (factor, index) => (
                          <div
                            key={index}
                            className="p-4 border border-slate-200/60 rounded-xl bg-gradient-to-br from-white to-slate-50/50 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">
                                {factor.factor}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${getRiskBadgeClass(factor.rating)}`}
                              >
                                {factor.rating}
                              </span>
                            </div>
                            {factor.notes && (
                              <p className="text-xs text-gray-500 mt-1">
                                {factor.notes}
                              </p>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* EDD Requirements */}
              {latestAssessment?.enhancedDueDiligenceRequired && (
                <div className="p-5 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200/60 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-orange-600 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div>
                      <h5 className="font-medium text-orange-800">
                        Enhanced Due Diligence Required
                      </h5>
                      {latestAssessment.eddMeasures &&
                        latestAssessment.eddMeasures.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {latestAssessment.eddMeasures.map(
                              (measure, index) => (
                                <li
                                  key={index}
                                  className="text-sm text-orange-700 flex items-center gap-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                  {measure}
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No assessment data
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create a new assessment to get started.
              </p>
            </div>
          )
        ) : activeTab === "history" ? (
          // Assessment History Tab
          <div className="space-y-4">
            {riskAssessments.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                No assessment history available
              </p>
            ) : (
              riskAssessments.map((ra) => (
                <div
                  key={ra.id}
                  className="border border-slate-200/60 rounded-xl overflow-hidden bg-gradient-to-br from-white to-slate-50/30 hover:shadow-md transition-shadow"
                >
                  <div
                    className="px-4 py-3 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 flex items-center justify-between cursor-pointer"
                    onClick={() =>
                      setExpandedAssessment(
                        expandedAssessment === ra.id ? null : ra.id,
                      )
                    }
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded border capitalize ${getRiskBadgeClass(ra.overallRiskRating)}`}
                      >
                        {ra.overallRiskRating}
                      </span>
                      {/* AL-437: rendered only when supplied — never defaulted */}
                      {ra.assessmentDate && (
                        <span className="text-sm text-gray-900">
                          {formatDateShort(ra.assessmentDate)}
                        </span>
                      )}
                      {ra.assessedBy && (
                        <span className="text-sm text-gray-500">
                          by {ra.assessedBy}
                        </span>
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transform transition-transform ${expandedAssessment === ra.id ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>

                  {expandedAssessment === ra.id && (
                    <div className="p-4 space-y-4">
                      {/* Risk Categories — AL-437.
                          The CDD report emits no factors block, so these are normally
                          absent and the whole grid is suppressed. They previously
                          defaulted to 'low', presenting four affirmative Low findings
                          to the C1 where no assessment had been made. Delivery Channel
                          Risk is gone: it was a hardcoded literal with no data source.
                          Do not reintroduce a default here — see the javadoc on
                          CddReportAssembler.riskRatingLabel for why the least-severe
                          value is the dangerous direction to guess in. */}
                      {(ra.productServiceRisk ||
                        ra.geographicRisk ||
                        ra.industryRisk) && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {ra.productServiceRisk && (
                            <div className="p-3 bg-gray-50 rounded">
                              <span className="text-xs text-gray-500">
                                Product/Service Risk
                              </span>
                              <div
                                className={`text-sm font-medium capitalize text-${getRiskColor(ra.productServiceRisk)}-600`}
                              >
                                {ra.productServiceRisk}
                              </div>
                            </div>
                          )}
                          {ra.geographicRisk && (
                            <div className="p-3 bg-gray-50 rounded">
                              <span className="text-xs text-gray-500">
                                Geographic Risk
                              </span>
                              <div
                                className={`text-sm font-medium capitalize text-${getRiskColor(ra.geographicRisk)}-600`}
                              >
                                {ra.geographicRisk}
                              </div>
                            </div>
                          )}
                          {ra.industryRisk && (
                            <div className="p-3 bg-gray-50 rounded">
                              <span className="text-xs text-gray-500">
                                Industry Risk
                              </span>
                              <div
                                className={`text-sm font-medium capitalize text-${getRiskColor(ra.industryRisk)}-600`}
                              >
                                {ra.industryRisk}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Notes */}
                      {ra.notes && (
                        <div className="p-3 bg-gray-50 rounded">
                          <span className="text-xs text-gray-500 block mb-1">
                            Notes
                          </span>
                          <p className="text-sm text-gray-700">{ra.notes}</p>
                        </div>
                      )}

                      {/* Approval Info */}
                      {ra.approvedBy && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <svg
                            className="w-4 h-4 text-green-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Approved by {ra.approvedBy} on{" "}
                          {ra.approvalDate
                            ? formatDateShort(ra.approvalDate)
                            : "-"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : null}
        {/* AL-352: question-level responses are intentionally unavailable
            without an authoritative assessment id. */}
      </div>
    </div>
  );
};

export default CustomerAssessment;
